import { useReportBuilderStore } from "../../logic/stores/reportBuilderStore.js";
import { REPORT_STEPS } from "../../constants/reportBuilder.js";
import {
  buildQueryPreview,
  buildReportConfig,
  getAdjacentStep,
  validateStep,
} from "../services/reportBuilderSummary.js";
import { useCallback, useMemo } from "react";
import { saveReport } from "../../api/endpoints/reportBuilder.js";

export function useReportBuilder() {
  const step = useReportBuilderStore((s) => s.step);
  const datasetId = useReportBuilderStore((s) => s.datasetId);
  const datasets = useReportBuilderStore((s) => s.datasets);
  const datasetsLoading = useReportBuilderStore((s) => s.datasetsLoading);
  const activeDataset = useReportBuilderStore((s) => s.activeDataset);
  const filters = useReportBuilderStore((s) => s.filters);
  const filterLogic = useReportBuilderStore((s) => s.filterLogic);
  const session = useReportBuilderStore((s) => s.session);
  const calculatedFields = useReportBuilderStore((s) => s.calculatedFields);
  const groupBy = useReportBuilderStore((s) => s.groupBy);
  const metrics = useReportBuilderStore((s) => s.metrics);
  const visualization = useReportBuilderStore((s) => s.visualization);
  const reportName = useReportBuilderStore((s) => s.reportName);
  const estimate = useReportBuilderStore((s) => s.estimate);
  const estimateLoading = useReportBuilderStore((s) => s.estimateLoading);
  const saving = useReportBuilderStore((s) => s.saving);
  const saveSuccess = useReportBuilderStore((s) => s.saveSuccess);
  const saveError = useReportBuilderStore((s) => s.saveError);

  const setStep = useReportBuilderStore((s) => s.setStep);
  const setDatasetId = useReportBuilderStore((s) => s.setDatasetId);
  const setSessionStartQuery = useReportBuilderStore((s) => s.setSessionStartQuery);
  const setSessionEndQuery = useReportBuilderStore((s) => s.setSessionEndQuery);
  const setVisualization = useReportBuilderStore((s) => s.setVisualization);
  const setReportName = useReportBuilderStore((s) => s.setReportName);
  const setFilterLogic = useReportBuilderStore((s) => s.setFilterLogic);
  const addFilter = useReportBuilderStore((s) => s.addFilter);
  const updateFilter = useReportBuilderStore((s) => s.updateFilter);
  const removeFilter = useReportBuilderStore((s) => s.removeFilter);
  const addCalculatedField = useReportBuilderStore((s) => s.addCalculatedField);
  const updateCalculatedField = useReportBuilderStore((s) => s.updateCalculatedField);
  const removeCalculatedField = useReportBuilderStore((s) => s.removeCalculatedField);
  const addFieldLogic = useReportBuilderStore((s) => s.addFieldLogic);
  const updateFieldLogic = useReportBuilderStore((s) => s.updateFieldLogic);
  const removeFieldLogic = useReportBuilderStore((s) => s.removeFieldLogic);
  const toggleGroupBy = useReportBuilderStore((s) => s.toggleGroupBy);
  const addMetric = useReportBuilderStore((s) => s.addMetric);
  const addMetricFromPreset = useReportBuilderStore((s) => s.addMetricFromPreset);
  const cloneMetric = useReportBuilderStore((s) => s.cloneMetric);
  const moveMetric = useReportBuilderStore((s) => s.moveMetric);
  const updateMetric = useReportBuilderStore((s) => s.updateMetric);
  const removeMetric = useReportBuilderStore((s) => s.removeMetric);
  const setSaving = useReportBuilderStore((s) => s.setSaving);
  const setSaveSuccess = useReportBuilderStore((s) => s.setSaveSuccess);
  const setSaveError = useReportBuilderStore((s) => s.setSaveError);
  const resetSaveState = useReportBuilderStore((s) => s.resetSaveState);

  const state = useMemo(
    () => ({
      step,
      datasetId,
      filterLogic,
      filters,
      session,
      calculatedFields,
      groupBy,
      metrics,
      visualization,
      reportName,
    }),
    [step, datasetId, filterLogic, filters, session, calculatedFields, groupBy, metrics, visualization, reportName],
  );

  const config = useMemo(() => buildReportConfig(state), [state]);
  const currentStep = useMemo(() => REPORT_STEPS.find((s) => s.id === step) ?? REPORT_STEPS[0], [step]);
  const stepIndex = REPORT_STEPS.findIndex((s) => s.id === step);
  const stepError = validateStep(step, state);
  const queryPreview = buildQueryPreview(config, activeDataset);

  const goToStep = useCallback(
    (stepId) => {
      const targetIdx = REPORT_STEPS.findIndex((s) => s.id === stepId);
      const currentIdx = REPORT_STEPS.findIndex((s) => s.id === step);
      if (targetIdx <= currentIdx) {
        setStep(stepId);
        return;
      }
      for (let i = 0; i < targetIdx; i += 1) {
        const err = validateStep(REPORT_STEPS[i].id, state);
        if (err) return;
      }
      setStep(stepId);
    },
    [step, state, setStep],
  );

  const goNext = useCallback(() => {
    const next = getAdjacentStep(step, 1);
    if (next && !validateStep(step, state)) setStep(next);
  }, [step, state, setStep]);

  const goPrev = useCallback(() => {
    const prev = getAdjacentStep(step, -1);
    if (prev) setStep(prev);
  }, [step, setStep]);

  const handleSave = useCallback(async () => {
    if (validateStep("save", state)) return;
    setSaving(true);
    resetSaveState();
    try {
      await saveReport({ name: reportName, config });
      setSaveSuccess();
    } catch (err) {
      setSaveError(err?.message ?? "Failed to save report");
    }
  }, [state, reportName, config, setSaving, resetSaveState, setSaveSuccess, setSaveError]);

  return {
    steps: REPORT_STEPS,
    step,
    datasetId,
    datasets,
    datasetsLoading,
    dataset: activeDataset,
    filters,
    filterLogic,
    session,
    calculatedFields,
    groupBy,
    metrics,
    visualization,
    reportName,
    currentStep,
    stepIndex,
    stepError,
    config,
    queryPreview,
    estimate,
    estimateLoading,
    saving,
    saveSuccess,
    saveError,
    goToStep,
    goNext,
    goPrev,
    handleSave,
    setDatasetId,
    setSessionStartQuery,
    setSessionEndQuery,
    setVisualization,
    setReportName,
    setFilterLogic,
    addFilter,
    updateFilter,
    removeFilter,
    addCalculatedField,
    updateCalculatedField,
    removeCalculatedField,
    addFieldLogic,
    updateFieldLogic,
    removeFieldLogic,
    toggleGroupBy,
    addMetric,
    addMetricFromPreset,
    cloneMetric,
    moveMetric,
    updateMetric,
    removeMetric,
  };
}

export { useReportBuilderBootstrap } from "./useReportBuilderBootstrap.js";
